/**
 * [T-050] Knowledge base contracts — W4-CONTRACTS §4 (09 §2)
 * ใช้ร่วม worker (src/kb/**) ↔ web (T-055) — ห้ามนิยามซ้ำในสองฝั่ง
 */
import { z } from "zod";

export const KbVisibility = z.enum(["public", "authenticated", "staff", "team"]);
export type KbVisibility = z.infer<typeof KbVisibility>;

export const KbArticleStatus = z.enum(["draft", "review", "published", "archived"]);
export type KbArticleStatus = z.infer<typeof KbArticleStatus>;

export const KbI18n = z.record(z.string(), z.string());

export const KbCategory = z.object({
  id: z.uuid(),
  parent_id: z.uuid().nullable(),
  slug: z.string(),
  name: KbI18n,
  description: KbI18n.nullable(),
  icon: z.string().nullable(),
  sort: z.number().int(),
  visibility: KbVisibility,
  team_id: z.uuid().nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  /** [Lead/T-059] จำนวนบทความ published ที่ผู้เรียกมองเห็น — มีเฉพาะ GET /api/v1/kb/categories (public) */
  article_count: z.number().int().nonnegative().optional(),
});
export type KbCategory = z.infer<typeof KbCategory>;

/** [Lead/T-059] การ์ดบทความแบบย่อสำหรับหน้า portal — ใช้ทั้ง GET /api/v1/kb/top และ GET /api/v1/kb/articles?category= */
export const KbArticleBrief = z.object({
  slug: z.string(),
  title: z.string(),
  excerpt: z.string().nullable(),
  lang: z.string(),
  views: z.number().int(),
  category: z.object({ id: z.uuid(), slug: z.string(), name: KbI18n }).nullable(),
});
export type KbArticleBrief = z.infer<typeof KbArticleBrief>;

/** ชื่อเดิมของ KbArticleBrief (GET /api/v1/kb/top) */
export const KbTopArticle = KbArticleBrief;
export type KbTopArticle = KbArticleBrief;

export const KbCategoryInput = z.object({
  parent_id: z.uuid().nullable().optional(),
  slug: z.string().regex(/^[a-z0-9][a-z0-9-]{0,118}[a-z0-9]$/).optional(),
  name: KbI18n,
  description: KbI18n.nullable().optional(),
  icon: z.string().max(64).nullable().optional(),
  sort: z.number().int().optional(),
  visibility: KbVisibility.optional(),
  /** บังคับเมื่อ visibility = team */
  team_id: z.uuid().nullable().optional(),
});
export type KbCategoryInput = z.infer<typeof KbCategoryInput>;

export const KbCategoryPatch = KbCategoryInput.partial();
export type KbCategoryPatch = z.infer<typeof KbCategoryPatch>;

export const KbArticle = z.object({
  id: z.uuid(),
  slug: z.string(),
  category_id: z.uuid().nullable(),
  visibility: KbVisibility,
  status: KbArticleStatus,
  publish_at: z.iso.datetime().nullable(),
  expire_at: z.iso.datetime().nullable(),
  default_lang: z.string().max(8),
  tags: z.array(z.string()),
  views: z.number().int(),
  helpful_yes: z.number().int(),
  helpful_no: z.number().int(),
  attach_count: z.number().int(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type KbArticle = z.infer<typeof KbArticle>;

export const KbArticleInput = z.object({
  slug: z.string().regex(/^[a-z0-9][a-z0-9-]{0,118}[a-z0-9]$/).optional(),
  category_id: z.uuid().nullable().optional(),
  visibility: KbVisibility.optional(),
  default_lang: z.string().max(8).optional(),
  tags: z.array(z.string().max(64)).max(20).optional(),
  publish_at: z.iso.datetime().nullable().optional(),
  expire_at: z.iso.datetime().nullable().optional(),
});
export type KbArticleInput = z.infer<typeof KbArticleInput>;

export const KbArticlePatch = KbArticleInput.partial();
export type KbArticlePatch = z.infer<typeof KbArticlePatch>;

export const KbVersion = z.object({
  id: z.uuid(),
  article_id: z.uuid(),
  lang: z.string().max(8),
  version: z.number().int(),
  title: z.string(),
  body_html: z.string(),
  excerpt: z.string().nullable(),
  is_current: z.boolean(),
  machine_translated: z.boolean(),
  created_at: z.iso.datetime(),
});
export type KbVersion = z.infer<typeof KbVersion>;

/** เวอร์ชันใหม่ = draft ของภาษานั้น (publish ภายหลัง → is_current สลับ + enqueue kb.embed) */
export const KbVersionInput = z.object({
  lang: z.string().min(2).max(8),
  title: z.string().trim().min(1).max(300),
  body_html: z.string().max(500_000),
  body_json: z.record(z.string(), z.unknown()).nullable().optional(),
  excerpt: z.string().max(2000).nullable().optional(),
  machine_translated: z.boolean().optional(),
});
export type KbVersionInput = z.infer<typeof KbVersionInput>;

/** รูปแบบที่ portal/web อ่าน — body_html ถูก sanitize ตอน save แล้ว */
export const KbArticlePublic = z.object({
  /** [Lead #76] id ของบทความ — portal ใช้ส่ง `article_id` ตอนบันทึก deflection/feedback */
  id: z.uuid(),
  slug: z.string(),
  title: z.string(),
  body_html: z.string(),
  excerpt: z.string().nullable(),
  lang: z.string(),
  available_langs: z.array(z.string()),
  category: z.object({ id: z.uuid(), slug: z.string(), name: KbI18n }).nullable(),
  updated_at: z.iso.datetime(),
  helpful: z.object({ yes: z.number().int(), no: z.number().int() }),
});
export type KbArticlePublic = z.infer<typeof KbArticlePublic>;

export const KbSearchQuery = z.object({
  q: z.string().trim().min(1).max(300),
  lang: z.string().max(8).optional(),
  limit: z.coerce.number().int().min(1).max(20).default(10),
});
export type KbSearchQuery = z.infer<typeof KbSearchQuery>;

export const KbSearchResult = z.object({
  article: KbArticle,
  version: KbVersion,
  score: z.number(),
  highlight: z.string().nullable().optional(),
});
export type KbSearchResult = z.infer<typeof KbSearchResult>;

export const KbTransitionInput = z.object({ to: KbArticleStatus });
export type KbTransitionInput = z.infer<typeof KbTransitionInput>;

export const KbAttachInput = z.object({ ticket_id: z.uuid() });
export type KbAttachInput = z.infer<typeof KbAttachInput>;

export const KbFeedbackInput = z.object({
  helpful: z.boolean(),
  comment: z.string().max(2000).nullable().optional(),
  lang: z.string().max(8).optional(),
});
export type KbFeedbackInput = z.infer<typeof KbFeedbackInput>;

export const KbDeflectionInput = z.object({
  article_id: z.uuid(),
  form_id: z.uuid().nullable().optional(),
  converted: z.boolean().optional(),
});
export type KbDeflectionInput = z.infer<typeof KbDeflectionInput>;
